// data/media-paths.js — direct CDN-base + filename concatenation.
//
// RM has no stem-scan dance. Filenames in the JSON match filenames on disk
// exactly: `RM0101PY` → `Media/RM0101PY.m4a`. (No `FF_EP003_` prefixes; no
// extension fallbacks.) `media-paths.js` is therefore tiny — just a CDN-base +
// filename concatenation.
//
// Brief 002: the publish pipeline produces both `.m4a` (master) and `.ogg`
// (Vorbis fallback) for every audio file. The player picks via a `<source>`
// list rather than a client-side rewrite. See `resolveAudioSources` below.

// CONTENT_BASE is injected at build time via esbuild's --define (see
// build.mjs). In dev (no define), falls back to "/content" so the dev
// server serves from the project root. In production, esbuild substitutes
// the production CDN URL.
//
// Absolute path so audio URLs resolve correctly from any SPA route. See the
// matching note in `data/content-loader.js`.
const CONTENT_BASE =
  // eslint-disable-next-line no-undef
  typeof __CONTENT_BASE__ !== "undefined" ? __CONTENT_BASE__ : "/content";

/**
 * Resolve a single audio URL — useful for unit tests and one-off scripts.
 * The player itself calls `resolveAudioSources` so the browser can fall
 * back through formats.
 *
 * @param {string} mediaFilename — exact filename, e.g. "RM0101PY.m4a"
 * @returns {string}
 */
export function resolveAudio(mediaFilename) {
  return `${CONTENT_BASE}/Media/${mediaFilename}`;
}

/**
 * Resolve the ordered list of `<source>` candidates for an audio item.
 *
 * Order matters — the browser picks the first format it can decode. `.m4a`
 * (AAC) ships first because the iOS production masters are AAC and that's
 * the canonical user-facing audio. `.ogg` (Opus-in-Ogg) is the fallback
 * for browsers without AAC codec support (notably Firefox-on-Linux).
 *
 * The `.ogg` MIME explicitly declares `codecs=opus` so browsers can do
 * format selection without downloading the file. The publish pipeline
 * produces Opus inside Ogg containers (libvorbis is unavailable in the
 * default Homebrew ffmpeg, and Opus is materially better than Vorbis at
 * speech bitrates anyway).
 *
 * @param {string} mediaFilename — exact filename, e.g. "RM0101PY.m4a"
 * @returns {Array<{url: string, type: string}>}
 */
export function resolveAudioSources(mediaFilename) {
  const stem = mediaFilename.replace(/\.[^.]+$/, "");
  return [
    { url: `${CONTENT_BASE}/Media/${stem}.m4a`, type: "audio/mp4" },
    { url: `${CONTENT_BASE}/Media/${stem}.ogg`, type: 'audio/ogg; codecs="opus"' },
  ];
}

/**
 * Resolve a video file URL. Same pattern as audio.
 * @param {string} mediaFilename
 * @returns {string}
 */
export function resolveVideo(mediaFilename) {
  return `${CONTENT_BASE}/Media/${mediaFilename}`;
}

/**
 * Resolve the ordered list of `<source>` candidates for a video item.
 *
 * Mirrors `resolveAudioSources`: `.mp4` (H.264/AAC) first because it's the
 * universal baseline every browser and iOS Safari can decode; `.webm`
 * (VP9/Opus) second as a smaller-file fallback for browsers that prefer it.
 * The browser picks the first format it can play. The publish pipeline is
 * expected to produce both for any video item; if only the `.mp4` exists,
 * the browser just uses it.
 *
 * @param {string} mediaFilename — exact filename, e.g. "RM123INTRO.mp4"
 * @returns {Array<{url: string, type: string}>}
 */
export function resolveVideoSources(mediaFilename) {
  const stem = mediaFilename.replace(/\.[^.]+$/, "");
  return [
    { url: `${CONTENT_BASE}/Media/${stem}.mp4`, type: "video/mp4" },
    { url: `${CONTENT_BASE}/Media/${stem}.webm`, type: "video/webm" },
  ];
}

/**
 * Resolve an HTML page URL. HTML items live under `Media/` alongside audio.
 * @param {string} mediaFilename
 * @returns {string}
 */
export function resolveHtml(mediaFilename) {
  return `${CONTENT_BASE}/Media/${mediaFilename}`;
}
